/**
 * Der Fokus eines Alarms: welche Produkte, welche Projekte.
 *
 * Der Picker schrieb „alles gewählt" als `_all` **neben** jede einzelne Id. Der Server verwirft das
 * Sentinel seit tri-server #1223 und wertet die Ids daneben — „Alle" hieß damit in Wahrheit „die
 * Optionen, die es beim Speichern gab": Ein später angelegtes Projekt war nicht erfasst, während der
 * Picker weiter „Alle" anzeigte (#1225).
 *
 * Deshalb gilt hier: **leere Liste = keine Einschränkung = alle**, so wie der Server sie liest. Das
 * Sentinel wird nicht mehr geschrieben und beim Laden weggeworfen; Bestandsregeln, die es noch
 * tragen, zeigen und speichern die Ids, die neben ihm stehen.
 *
 * **„Alle" und „jedes Häkchen gesetzt" sind dabei nicht dasselbe.** Leer heißt beim Server nicht
 * „jedes Projekt", sondern „gar keine Einschränkung": `matchesProjectScope`/`matchesProductScope`
 * (tri-server `src/businessRuleScope.ts`) lassen bei leerer Liste jedes Asset durch — auch die, die
 * in keinem Projekt stehen und kein Produkt tragen. Eine Auswahl, die heute zufällig alle Optionen
 * abdeckt, zur leeren Liste zusammenzufassen, hübe also genau die Einschränkung auf, die der Mandant
 * gewählt hat (die Flut aus #1181/#1182). Die leere Liste schreiben nur der „Alle"-Eintrag selbst
 * und das abgewählte letzte Häkchen — beides ein bewusster Griff zu „Alle"; auf das zweite weist
 * der Picker eigens hin.
 */
export const ALL_SENTINEL = '_all';

/** Was der Picker aus einer gespeicherten Regel anzeigt — ohne das abgelegte Sentinel. */
export const selectionFromRule = (keys?: string[] | null): string[] =>
    (keys || []).filter((key) => key !== ALL_SENTINEL);

/**
 * Ob die Auswahl „alle" bedeutet — und das ist allein die leere Liste, so wie der Server sie liest.
 *
 * Eine Liste, die jede heutige Option aufzählt, ist eine Einschränkung auf genau diese Ids: Der
 * Auslesetext nennt sie deshalb beim Namen, statt „Alle" zu behaupten und morgen etwas anderes zu
 * meinen.
 */
export const isAllSelected = (keys: string[] | null | undefined): boolean =>
    selectionFromRule(keys).length === 0;

/**
 * Was gespeichert wird: die gewählten Ids, ohne das abgelegte Sentinel.
 *
 * Zusammengefasst wird nichts. „Alle" kommt hier bereits als leere Liste an — der Picker schreibt
 * sie, wenn der „Alle"-Eintrag geklickt oder das letzte Häkchen abgewählt wird —, und eine Auswahl,
 * die alle Optionen aufzählt, bleibt die Aufzählung: Sie ist eine Einschränkung und darf keine
 * werden, die der Server als „ohne Einschränkung" liest.
 */
export const selectionForSave = (keys: string[] | null | undefined): string[] =>
    selectionFromRule(keys);
